import { useCallback } from 'react';
import { isCapacityFull, PLAN_LABEL } from '../../components/plan/planLabels';
import { usePlanName } from '../../components/plan/usePlan';
import { requestRefresh } from '../../lib/resourceRefresh';
import type { KbCapacityItem, KbCapacityKey, PlanName } from '../../types/api';
import { useKbReadiness } from './useKbReadiness';

export interface SectionCapacity {
  /** This section's count against its cap, or null while unknown. */
  capacity: KbCapacityItem | null;
  /** At or above the cap. False while unknown: the backend's 402 still guards. */
  isFull: boolean;
  /** The account's plan, for the cap message; null while unknown. */
  planName: PlanName | null;
  /** Tooltip for the disabled add button at the cap, undefined below it. */
  fullTitle: string | undefined;
  /**
   * Re-reads the counts after a create or a delete. Goes through the
   * `kb_readiness` topic, so the readiness card re-reads with it.
   */
  refreshCapacity: () => void;
}

/**
 * How full one capped brand-profile section is, read from `kb-readiness`
 * (the same numbers the assistant reports). Never hides the section: at the
 * cap only the add action is disabled, and rows already on file stay usable.
 */
export function useSectionCapacity(businessId: string, key: KbCapacityKey): SectionCapacity {
  const { capacity } = useKbReadiness(businessId);
  const planName = usePlanName();
  const item = capacity?.[key] ?? null;
  const isFull = item !== null && isCapacityFull(item);

  const refreshCapacity = useCallback(() => {
    requestRefresh(businessId, ['kb_readiness']);
  }, [businessId]);

  return {
    capacity: item,
    isFull,
    planName,
    fullTitle: isFull
      ? `Full on ${planName ? PLAN_LABEL[planName] : 'your plan'}. Upgrade to add more.`
      : undefined,
    refreshCapacity,
  };
}
